import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import TopBar from "@/components/TopBar";
import { api } from "@/lib/api";
import { useBilling } from "@/context/BillingContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, Sparkles, ArrowLeft } from "lucide-react";

const PLAN_ORDER = ["free", "pro", "school"];

const PLAN_TO_LOOKUP = {
  pro: "pro_monthly",
  school: "school_yearly",
};

function PlanCard({ plan, current, onPick, busy }) {
  const isCurrent = plan.id === current;
  const highlighted = plan.recommended;
  return (
    <div
      data-testid={`plan-card-${plan.id}`}
      className={`relative bg-surface border p-8 flex flex-col ${highlighted ? "border-amber shadow-[0_0_0_1px_#F59E0B_inset]" : "border-surface-3"}`}
    >
      {highlighted && (
        <div className="absolute -top-3 left-6 bg-amber text-[#0B0D12] text-[10px] font-condensed uppercase tracking-[0.3em] px-2 py-1">
          Recommended
        </div>
      )}
      <div className="flex items-baseline justify-between">
        <div className="font-display text-3xl uppercase tracking-wide text-white">{plan.label}</div>
        {isCurrent && (
          <span className="text-[10px] font-condensed uppercase tracking-widest text-emerald-400 border border-emerald-500/40 px-2 py-1">
            Current
          </span>
        )}
      </div>
      <div className="mt-2">
        <span className="font-display text-4xl text-amber">{plan.price}</span>
      </div>
      <ul className="mt-6 space-y-2 flex-1">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm text-slate-300">
            <Check className="w-4 h-4 text-amber mt-0.5 shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <div className="mt-8">
        {isCurrent ? (
          <div className="w-full h-11 bg-surface-2 border border-surface-3 flex items-center justify-center font-condensed uppercase tracking-[0.25em] text-xs text-slate-400">
            Your plan
          </div>
        ) : (
          <Button
            onClick={() => onPick(plan.id)}
            disabled={busy || plan.id === "free"}
            data-testid={`plan-pick-${plan.id}`}
            className={`w-full h-11 rounded-none font-condensed uppercase tracking-[0.25em] text-xs ${
              highlighted
                ? "bg-amber hover:bg-amber-600 text-[#0B0D12]"
                : "bg-surface-2 hover:bg-surface-3 text-white border border-surface-3"
            }`}
          >
            {plan.id === "free" ? "Free — Automatic" : busy ? "Switching…" : `Switch to ${plan.label}`}
          </Button>
        )}
      </div>
    </div>
  );
}

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const { status, refresh } = useBilling();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/billing/plans"); setPlans(data.plans); }
      catch (e) {
        console.error("Failed to load plans", e);
        toast.error("Failed to load plans. Please try again.");
      }
    })();
  }, []);

  const currentEffective = status?.effective_plan || "free";

  const pick = async (planId) => {
    setBusy(true);
    try {
      const lookupKey = PLAN_TO_LOOKUP[planId];
      if (lookupKey) {
        // Real Stripe Checkout
        const { data } = await api.post("/payments/checkout", {
          lookup_key: lookupKey,
          origin_url: window.location.origin,
        });
        if (data.checkout_url) {
          window.location.href = data.checkout_url;
          return;
        }
      } else {
        // Free plan — direct downgrade
        await api.post("/billing/change-plan", { plan: planId });
        await refresh();
        toast.success(`Switched to ${plans.find(p => p.id === planId)?.label || planId}`);
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Change failed"));
    } finally { setBusy(false); }
  };

  const visiblePlans = PLAN_ORDER.map(id => plans.find(p => p.id === id)).filter(Boolean);

  return (
    <div data-testid="pricing-page">
      <TopBar
        title="Pricing"
        subtitle="Built for coaches. Priced for programs."
        actions={
          <Link to="/settings/billing" data-testid="pricing-manage-link"
            className="text-[10px] font-condensed uppercase tracking-[0.28em] text-amber hover:underline flex items-center gap-1">
            <ArrowLeft className="w-3 h-3" /> Manage current plan
          </Link>
        }
      />
      <div className="p-8 max-w-6xl">
        <div className="mb-8">
          <div className="text-[10px] font-condensed uppercase tracking-[0.3em] text-amber">Live Stripe Checkout · Test Mode</div>
          <div className="font-display text-5xl tracking-wide text-white uppercase leading-tight mt-2">
            One System. Every Program.
          </div>
          <p className="text-slate-300 mt-3 max-w-2xl">
            Start free with a 14-day Pro Coach trial. Real Stripe checkout — use test card <span className="font-mono text-amber">4242 4242 4242 4242</span>, any future expiry, any CVC.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {visiblePlans.map(plan => (
            <PlanCard key={plan.id} plan={plan} current={currentEffective} onPick={pick} busy={busy} />
          ))}
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8 text-sm text-slate-400">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-amber" />
              <div className="font-condensed uppercase tracking-[0.25em] text-white text-sm">Program-aware AI</div>
            </div>
            <p>The AI Coach that learns your mission, playbook, and quote library — not generic football advice.</p>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-amber" />
              <div className="font-condensed uppercase tracking-[0.25em] text-white text-sm">Everything In One Place</div>
            </div>
            <p>Practice cards, playbook diagrams, film links, culture curriculum, and staff manual — one command center.</p>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-amber" />
              <div className="font-condensed uppercase tracking-[0.25em] text-white text-sm">Shared With Your Staff</div>
            </div>
            <p>School Plan lets your entire coaching staff work off the same install, standards, and film library.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
